import React from "react"
import { PageProps } from "gatsby"

import Layout from "../components/layout"

/**
 * Privacy policy.
 *
 * Every statement here is meant to match what the site actually does. If the
 * site starts loading something new — a form, an embed, an analytics script —
 * this page has to be updated in the same change, and a consent mechanism has
 * to come back with it.
 *
 * Verified against the built output at the time of writing:
 *  - no analytics, no advertising, no profiling, no third-party script at all;
 *  - no contact form, no accounts, no payments, no newsletter, no comments;
 *  - webfonts are self-hosted, so no request goes to Google for fonts;
 *  - social links are plain anchors — no widgets, no embeds;
 *  - zero cookies and zero browser storage: a clean session walking every page
 *    type ended with an empty cookie jar and empty localStorage/sessionStorage.
 *
 * Note: layout.tsx still contains a theme-switch handler that would write a
 * "theme-v2" localStorage key, but no control in the UI calls it. If a theme
 * toggle is ever surfaced, section 5 below stops being true and must change.
 */
const LAST_UPDATED = "3 sierpnia 2026"

export default ({ location }: PageProps) => {
    return (
        <Layout
            seo={{
                title: "Polityka prywatności i plików cookies | Horse Physio",
                fullTitle: true,
                description:
                    "Informacje o zasadach przetwarzania danych osobowych i wykorzystywania plików cookies w serwisie Horse Physio.",
            }}
            location={location}
        >
            <div className="large-container">
                <header className="pt-20 pb-10 lg:pt-32 lg:pb-12">
                    <div className="measure">
                        <p className="eyebrow">Informacja prawna</p>
                        <h1 className="display-2 text-color-1 mt-5">
                            Polityka prywatności i plików cookies
                        </h1>
                        <p className="meta mt-8">
                            Data ostatniej aktualizacji: {LAST_UPDATED}
                        </p>
                        <div className="rule-short mt-8"></div>
                    </div>
                </header>

                <div className="measure post-content pb-24 lg:pb-32">
                    <h2>1. Informacje ogólne</h2>
                    <p>
                        Niniejsza polityka opisuje zasady przetwarzania danych
                        osobowych oraz wykorzystywania plików cookies i
                        podobnych technologii w serwisie internetowym Horse
                        Physio, dostępnym pod adresem horsephysio.pl, dalej jako
                        „Serwis”.
                    </p>
                    <p>
                        Serwis ma charakter informacyjny i prezentuje usługi
                        związane z fizjoterapią oraz rehabilitacją koni.
                    </p>
                    <p>
                        Serwis nie umożliwia zakładania kont użytkowników,
                        dokonywania płatności ani wysyłania wiadomości za pomocą
                        formularza kontaktowego. Nie prowadzi newslettera,
                        systemu rezerwacji ani sekcji komentarzy. Kontakt odbywa
                        się poza Serwisem — pocztą elektroniczną lub
                        telefonicznie.
                    </p>

                    <h2>2. Administrator danych</h2>
                    <p>
                        Administratorem danych osobowych jest Dorota Chojecka,
                        prowadząca serwis Horse Physio, NIP: 7010188463.
                    </p>
                    <p>Z administratorem można skontaktować się:</p>
                    <ul>
                        <li>
                            pocztą elektroniczną:{" "}
                            <a href="mailto:dorota.horsephysio@gmail.com">
                                dorota.horsephysio@gmail.com
                            </a>
                            ;
                        </li>
                        <li>
                            telefonicznie:{" "}
                            <a href="tel:695492704">695 492 704</a>.
                        </li>
                    </ul>

                    <h2>3. Kontakt e-mailowy i telefoniczny</h2>
                    <p>
                        W przypadku kontaktu za pośrednictwem poczty
                        elektronicznej lub telefonu administrator może
                        przetwarzać dane przekazane dobrowolnie przez osobę
                        kontaktującą się, w szczególności:
                    </p>
                    <ul>
                        <li>imię i nazwisko;</li>
                        <li>adres e-mail;</li>
                        <li>numer telefonu;</li>
                        <li>treść wiadomości;</li>
                        <li>
                            informacje niezbędne do ustalenia miejsca i terminu
                            wykonania usługi;
                        </li>
                        <li>
                            dane potrzebne do przygotowania, realizacji lub
                            rozliczenia usługi.
                        </li>
                    </ul>
                    <p>
                        Nie należy przesyłać danych, które nie są potrzebne do
                        udzielenia odpowiedzi albo realizacji usługi.
                    </p>
                    <p>Dane mogą być przetwarzane w celu:</p>
                    <ol>
                        <li>
                            udzielenia odpowiedzi na wiadomość lub inne
                            zapytanie — na podstawie prawnie uzasadnionego
                            interesu administratora polegającego na prowadzeniu
                            komunikacji i obsłudze zapytań (art. 6 ust. 1 lit. f
                            RODO);
                        </li>
                        <li>
                            ustalenia warunków wizyty albo podjęcia działań na
                            żądanie osoby przed zawarciem umowy (art. 6 ust. 1
                            lit. b RODO);
                        </li>
                        <li>
                            zawarcia i wykonania umowy dotyczącej świadczonych
                            usług (art. 6 ust. 1 lit. b RODO);
                        </li>
                        <li>
                            realizacji obowiązków księgowych, podatkowych lub
                            innych obowiązków wynikających z przepisów prawa
                            (art. 6 ust. 1 lit. c RODO);
                        </li>
                        <li>
                            ustalenia, dochodzenia lub obrony przed ewentualnymi
                            roszczeniami — na podstawie prawnie uzasadnionego
                            interesu administratora (art. 6 ust. 1 lit. f RODO).
                        </li>
                    </ol>
                    <p>
                        Podanie danych jest dobrowolne, jednak brak danych
                        potrzebnych do kontaktu lub realizacji usługi może
                        uniemożliwić udzielenie odpowiedzi, umówienie wizyty
                        albo wykonanie usługi.
                    </p>

                    <h2>4. Dane techniczne i logi serwera</h2>
                    <p>
                        Podczas korzystania z Serwisu mogą być automatycznie
                        przetwarzane dane techniczne przekazywane przez
                        przeglądarkę lub zapisywane w logach serwera, takie jak:
                    </p>
                    <ul>
                        <li>adres IP;</li>
                        <li>data i godzina żądania;</li>
                        <li>adres odwiedzonej podstrony;</li>
                        <li>
                            informacje o rodzaju przeglądarki i systemie
                            operacyjnym;
                        </li>
                        <li>
                            informacje o błędach oraz zdarzeniach związanych z
                            bezpieczeństwem Serwisu.
                        </li>
                    </ul>
                    <p>
                        Serwis jest stroną statyczną — nie posiada własnej bazy
                        danych ani warstwy aplikacyjnej zapisującej dane
                        odwiedzających. Administrator nie prowadzi własnych
                        logów i nie ma dostępu do surowych logów serwera.
                    </p>
                    <p>
                        Dane techniczne są przetwarzane wyłącznie po stronie
                        dostawcy hostingu, którym jest Netlify, Inc., działający
                        jako podmiot przetwarzający, w celu zapewnienia
                        prawidłowego i bezpiecznego działania Serwisu,
                        diagnozowania błędów oraz przeciwdziałania nadużyciom.
                        Netlify nie publikuje jednego, sztywnego okresu
                        przechowywania takich danych — ustala go zgodnie z
                        zasadami opisanymi w swojej polityce prywatności, w
                        oparciu o zakres, charakter i wrażliwość danych, po czym
                        dane są usuwane. Szczegółowe informacje w tym zakresie
                        można uzyskać, kontaktując się z administratorem.
                    </p>
                    <p>
                        Podstawą ich przetwarzania jest prawnie uzasadniony
                        interes administratora polegający na utrzymaniu
                        bezpieczeństwa, dostępności i prawidłowego działania
                        Serwisu (art. 6 ust. 1 lit. f RODO).
                    </p>

                    <h2>5. Pliki cookies i podobne technologie</h2>
                    <p>
                        Serwis nie zapisuje na Państwa urządzeniu żadnych plików
                        cookies. Nie korzysta również z innych technologii
                        przechowywania informacji w przeglądarce, takich jak
                        pamięć lokalna (localStorage) czy pamięć sesji
                        (sessionStorage). Odwiedzenie Serwisu nie pozostawia na
                        Państwa urządzeniu żadnych danych.
                    </p>
                    <p>
                        Serwis nie korzysta z narzędzi analitycznych ani
                        statystycznych, nie stosuje cookies marketingowych ani
                        reklamowych, nie prowadzi reklamy behawioralnej, nie
                        profiluje użytkowników i nie śledzi ich aktywności w
                        innych serwisach. Nie osadza map, filmów, widgetów
                        społecznościowych ani systemu komentarzy, nie
                        wykorzystuje mechanizmu reCAPTCHA i nie ładuje żadnych
                        skryptów, czcionek ani innych zasobów pochodzących od
                        podmiotów zewnętrznych. Wszystkie elementy strony, w tym
                        czcionki i ikony, są serwowane z serwera Serwisu,
                        dlatego korzystanie ze strony nie powoduje połączenia z
                        serwerami innych podmiotów.
                    </p>
                    <p>
                        Ponieważ Serwis nie wykorzystuje żadnych technologii
                        wymagających zgody, nie jest wyświetlany baner zgody na
                        cookies. Nie ma również potrzeby wyrażania ani
                        wycofywania jakiejkolwiek zgody w tym zakresie.
                    </p>
                    <p>
                        Jedynym połączeniem sieciowym, jakie powstaje podczas
                        przeglądania Serwisu, jest połączenie z serwerem, na
                        którym Serwis jest utrzymywany. Dotyczące go informacje
                        opisano w punkcie 4.
                    </p>

                    <h2>6. Odbiorcy danych</h2>
                    <p>
                        Dane osobowe mogą być przekazywane podmiotom
                        wspierającym administratora w prowadzeniu Serwisu i
                        działalności, w szczególności:
                    </p>
                    <ul>
                        <li>
                            dostawcy hostingu — Netlify, Inc., na którego
                            serwerach utrzymywany jest Serwis;
                        </li>
                        <li>
                            dostawcy poczty elektronicznej — Google Ireland
                            Limited (usługa Gmail), za pośrednictwem której
                            prowadzona jest korespondencja z adresu
                            dorota.horsephysio@gmail.com;
                        </li>
                        <li>
                            podmiotom świadczącym usługi księgowe, prawne lub
                            doradcze — jeżeli rozliczenie albo obsługa sprawy
                            tego wymaga;
                        </li>
                        <li>
                            organom publicznym, jeżeli obowiązek udostępnienia
                            danych wynika z przepisów prawa.
                        </li>
                    </ul>
                    <p>
                        Podmioty przetwarzające dane na zlecenie administratora
                        mogą korzystać z nich wyłącznie w zakresie niezbędnym do
                        wykonania powierzonych usług.
                    </p>

                    <h2>
                        7. Przekazywanie danych poza Europejski Obszar
                        Gospodarczy
                    </h2>
                    <p>
                        Dostawcy wskazani w punkcie 6 należą do grup
                        kapitałowych z siedzibą w Stanach Zjednoczonych, w
                        związku z czym dane mogą być przetwarzane poza
                        Europejskim Obszarem Gospodarczym. Dotyczy to hostingu
                        Serwisu (Netlify, Inc.) oraz poczty elektronicznej
                        Gmail (Google Ireland Limited).
                    </p>
                    <p>
                        Przekazanie danych odbywa się na podstawie mechanizmów
                        przewidzianych przez przepisy o ochronie danych
                        osobowych, w szczególności standardowych klauzul
                        umownych zatwierdzonych przez Komisję Europejską lub
                        decyzji stwierdzającej odpowiedni stopień ochrony.
                        Szczegółowe informacje dotyczące stosowanych
                        zabezpieczeń można uzyskać, kontaktując się z
                        administratorem.
                    </p>

                    <h2>8. Okres przechowywania danych</h2>
                    <p>
                        Dane są przechowywane tylko przez okres potrzebny do
                        realizacji celu, dla którego zostały zebrane. W
                        szczególności:
                    </p>
                    <ul>
                        <li>
                            korespondencja dotycząca zapytań jest przechowywana
                            przez czas potrzebny do udzielenia odpowiedzi i
                            zakończenia sprawy, a następnie może być
                            przechowywana do czasu upływu okresu przedawnienia
                            potencjalnych roszczeń;
                        </li>
                        <li>
                            dane związane z realizacją usług są przechowywane
                            przez okres wykonywania umowy, a następnie przez
                            czas wymagany przez przepisy lub potrzebny do
                            dochodzenia i obrony przed roszczeniami;
                        </li>
                        <li>
                            dokumenty księgowe i podatkowe są przechowywane
                            przez okres wymagany przepisami prawa;
                        </li>
                        <li>
                            dane techniczne i logi są przechowywane przez okres
                            wskazany w punkcie 4.
                        </li>
                    </ul>

                    <h2>9. Prawa osób, których dane dotyczą</h2>
                    <p>
                        Na zasadach określonych w przepisach o ochronie danych
                        osobie, której dane dotyczą, może przysługiwać prawo do:
                    </p>
                    <ul>
                        <li>dostępu do danych;</li>
                        <li>otrzymania kopii danych;</li>
                        <li>sprostowania danych;</li>
                        <li>usunięcia danych;</li>
                        <li>ograniczenia przetwarzania;</li>
                        <li>przenoszenia danych;</li>
                        <li>
                            wniesienia sprzeciwu wobec przetwarzania opartego na
                            prawnie uzasadnionym interesie;
                        </li>
                        <li>
                            wycofania zgody w dowolnym momencie, jeżeli
                            przetwarzanie odbywa się na podstawie zgody.
                        </li>
                    </ul>
                    <p>
                        Zakres poszczególnych praw zależy od podstawy i celu
                        przetwarzania.
                    </p>
                    <p>
                        W celu skorzystania ze swoich praw można skontaktować
                        się z administratorem pod adresem{" "}
                        <a href="mailto:dorota.horsephysio@gmail.com">
                            dorota.horsephysio@gmail.com
                        </a>
                        .
                    </p>
                    <p>
                        Osobie, która uważa, że jej dane są przetwarzane
                        niezgodnie z prawem, przysługuje również prawo
                        wniesienia skargi do Prezesa Urzędu Ochrony Danych
                        Osobowych (ul. Stawki 2, 00-193 Warszawa).
                    </p>

                    <h2>10. Zautomatyzowane podejmowanie decyzji</h2>
                    <p>
                        Dane osobowe nie są wykorzystywane do zautomatyzowanego
                        podejmowania decyzji ani profilowania, które wywoływałoby
                        wobec użytkownika skutki prawne lub w podobny sposób
                        istotnie na niego wpływało.
                    </p>

                    <h2>11. Zewnętrzne odnośniki</h2>
                    <p>
                        Serwis zawiera odnośniki do profili w serwisach Facebook
                        i Instagram. Są to zwykłe odnośniki tekstowe — ikony
                        pochodzą z Serwisu, a samo wyświetlenie strony nie
                        powoduje ładowania skryptów, pikseli ani widgetów tych
                        serwisów i nie przekazuje im żadnych informacji o
                        Państwa wizycie.
                    </p>
                    <p>
                        Po przejściu do zewnętrznego serwisu użytkownik podlega
                        zasadom prywatności określonym przez jego operatora.
                    </p>

                    <h2>12. Bezpieczeństwo danych</h2>
                    <p>
                        Administrator stosuje środki techniczne i organizacyjne
                        odpowiednie do charakteru przetwarzanych danych oraz
                        występujących zagrożeń.
                    </p>
                    <p>
                        Połączenie z Serwisem jest szyfrowane za pomocą
                        protokołu HTTPS. Należy pamiętać, że przesyłanie danych
                        przez Internet zawsze wiąże się z pewnym ryzykiem,
                        dlatego użytkownicy nie powinni przekazywać informacji,
                        które nie są potrzebne do udzielenia odpowiedzi lub
                        wykonania usługi.
                    </p>

                    <h2>13. Zmiany polityki</h2>
                    <p>
                        Polityka może być aktualizowana w szczególności w
                        przypadku zmiany sposobu działania Serwisu,
                        wykorzystywanych dostawców albo obowiązujących wymagań
                        prawnych.
                    </p>
                    <p>
                        Aktualna wersja polityki jest zawsze dostępna na tej
                        podstronie. Data ostatniej aktualizacji znajduje się na
                        początku dokumentu.
                    </p>
                </div>
            </div>
        </Layout>
    )
}
